import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { api, czk, media } from "../lib/api";
import { useCart } from "../lib/store";
import { Daisy } from "./Daisy";

const Suggestions = ({ items }) => {
  const { add } = useCart();
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { home: true } }).then((r) => setList(r.data));
  }, []);
  const ids = new Set(items.map((i) => i.product_id));
  const s = list.filter((p) => !ids.has(p.id)).slice(0, 2);
  if (!s.length) return null;
  return (
    <div className="mt-6">
      <p className="font-script text-xl text-[#D96B43]">Mohlo by se vám líbit</p>
      <div className="mt-2 space-y-2">
        {s.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-2xl bg-[#F7F3EA] p-2">
            <img src={media(p.images?.[0])} alt="" className="h-12 w-12 rounded-xl object-cover" />
            <p className="flex-1 text-sm">{p.name}</p>
            <button className="grid h-8 w-8 place-items-center rounded-full bg-[#A3AC86] text-white" onClick={() => add(p)} data-testid={`suggest-add-${p.slug}`}><Plus className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const nav = useNavigate();
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[60] bg-[#2D3426]/40 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
          <motion.aside data-testid="cart-drawer" onClick={(e) => e.stopPropagation()} initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 26, stiffness: 220 }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col rounded-l-[2rem] bg-white">
            <div className="flex items-center justify-between border-b p-6">
              <h2 className="h-section text-2xl">Váš košík</h2>
              <button onClick={() => setOpen(false)} className="grid h-10 w-10 place-items-center rounded-full bg-[#F7F3EA] transition-transform hover:rotate-90" data-testid="cart-close-btn"><X /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {!items.length ? (
                <div className="flex flex-col items-center py-16 text-center">
                  <span className="grid h-28 w-28 place-items-center rounded-full bg-[#A3AC86] animate-float"><Daisy size={80} /></span>
                  <p className="h-section mt-6 text-xl">Košík je zatím prázdný</p>
                  <p className="mt-1 text-sm text-[#6E7565]">Vyberte si z našich hravých materiálů.</p>
                  <button className="btn-sage mt-6" onClick={() => { setOpen(false); nav("/eshop"); }} data-testid="cart-empty-shop-btn">Do e-shopu</button>
                </div>
              ) : (
                <ul className="space-y-3">
                  <AnimatePresence initial={false}>
                    {items.map((i) => (
                      <motion.li key={i.product_id} layout initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -60, height: 0 }}
                        className="flex gap-3 rounded-2xl border p-3" data-testid={`cart-item-${i.slug}`}>
                        <img src={media(i.image)} alt="" className="h-16 w-16 rounded-xl object-cover" />
                        <div className="flex-1">
                          <p className="text-sm font-medium leading-snug">{i.name}</p>
                          <p className="text-sm font-bold text-[#7A8460]">{czk(i.price * i.qty)}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <button className="grid h-7 w-7 place-items-center rounded-full bg-[#F7F3EA]" onClick={() => setQty(i.product_id, i.qty - 1)} data-testid={`cart-dec-${i.slug}`}><Minus className="h-3 w-3" /></button>
                            <span className="w-5 text-center text-sm" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                            <button className="grid h-7 w-7 place-items-center rounded-full bg-[#F7F3EA]" onClick={() => setQty(i.product_id, i.qty + 1)} data-testid={`cart-inc-${i.slug}`}><Plus className="h-3 w-3" /></button>
                          </div>
                        </div>
                        <button onClick={() => remove(i.product_id)} className="self-start text-[#6E7565] hover:text-[#D96B43]" data-testid={`cart-remove-${i.slug}`}><Trash2 className="h-4 w-4" /></button>
                      </motion.li>
                    ))}
                  </AnimatePresence>
                </ul>
              )}
              <Suggestions items={items} />
            </div>
            {items.length > 0 && (
              <div className="border-t p-6">
                <div className="flex justify-between text-sm text-[#6E7565]"><span>Doprava</span><span>Zdarma – digitální produkt</span></div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-semibold">Celkem</span>
                  <span className="h-section text-2xl" data-testid="cart-total">{czk(total)}</span>
                </div>
                <button className="btn-sage mt-4 w-full" onClick={() => { setOpen(false); nav("/pokladna"); }} data-testid="cart-checkout-btn">
                  <ShoppingBag className="h-4 w-4" /> Pokračovat k pokladně
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
